import { createPortal } from "react-dom";

function humanise(str = "") {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
// ─── helpers ─────────────────────────────────────────────────────────────────
export const getInitials = (name = "") => {
  if (!name) return "NA";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const AVATAR_COLORS = [
  "#3b82f6",
  "#a855f7",
  "#14b8a6",
  "#f97316",
  "#22c55e",
  "#ef4444",
  "#eab308",
];
export const getAvatarColor = (name = "") => {
  if (!name) return "#6b7280";
  return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
export const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};
export const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  return "Just now";
};
export const formatINR = (n) =>
  n
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

// Stage / Status maps
export const STAGE_LABELS = {
  CASE_CREATED: "Case Created",
  SPOT_SURVEY: "Spot Survey",
  WORKSHOP_REPAIR: "Workshop Repair",
  SURVEY_PROCESS: "Survey",
  DELIVERY_PAYMENT: "Delivery & Payment",
  CLAIM_SETTLEMENT: "Claim Settlement",
};
export const STATUS_LABELS = {
  // CASE_CREATED stage
  CASE_REGISTERED: "Case Registered",
  CUSTOMER_REGISTERED: "Customer Registered",
  DOCUMENTS_PENDING: "Docs Pending",
  DOCUMENTS_SUBMITTED: "Docs Submitted",
  INTIMATION_RAISED: "Intimation Raised",
  // SURVEY_PROCESS stage
  SPOT_SURVEY_ASSIGNED: "Spot Survey Assigned",
  SPOT_SURVEY_COMPLETED: "Spot Survey Done",
  // WORKSHOP_REPAIR stage
  WORKSHOP_ASSIGNED: "Workshop Assigned",
  VEHICLE_PICKED: "Vehicle Picked",
  VEHICLE_RECEIVED_AT_WORKSHOP: "Vehicle Received",
  WORKSHOP_ESTIMATE: "Waiting For Workshop Estimate",
  ESTIMATE_APPROVED: "Estimate Approved",
  CLAIM_FORM_SENT_TO_CUSTOMER: "Claim Form Sent",
  CLAIM_FORM_SIGNED: "Claim Form Signed",
  CLAIM_FORM_APPROVED: "Claim Form Approved",
  CLAIM_SUBMITTED: "Claim Submitted",
  WORKSHOP_SURVEY_ASSIGNED: "Workshop 1st Survey Assigned",
  WORKSHOP_SURVEY_COMPLETED: "Workshop 1st Survey Done",
  DISMANTLING_APPROVED: "Dismantling Approved",
  DISMANTLING_COMPLETED: "Dismantling Done",
  SECOND_SURVEY_ASSIGNED: "Workshop 2nd Survey Assigned",
  SECOND_SURVEY_COMPLETED: "Workshop 2nd Survey Done",
  FINAL_SURVEY_COMPLETED: "Final Survey Done",
  WORK_STARTED: "Work Started",
  PART_ORDERED: "Parts Ordered",
  PART_DELIVERED: "Parts Delivered",
  VEHICLE_FINALIZED: "Vehicle Finalized",
  ROAD_TRIAL_COMPLETED: "Road Trial Done",
  READY_FOR_REINSPECTION: "Ready for Reinspection",
  REINSPECTION_COMPLETED: "Reinspection Done",
  PROFORMA_UPLOADED: "Proforma Uploaded",
  PROFORMA_RECEIVED: "Proforma Received",
  ASSESSMENT_RECEIVED: "Assessment Received",
  ASSESSMENT_CROSSCHECKED: "Assessment Crosschecked",
  TAX_INVOICE_RECEIVED: "Tax Invoice Received",
  // DELIVERY_PAYMENT stage
  VEHICLE_DELIVERED: "Vehicle Delivered",
  PAYMENT_REQUEST_SENT: "Payment Requested",
  PAYMENT_COMPLETED: "Payment Done",
  // CLAIM_SETTLEMENT stage
  CLAIM_SETTLED: "Claim Settled",
  CASE_CLOSED: "Case Closed",
  CANCELLED: "Cancelled",
};
export const stageBadge = (stage) =>
  ({
    CASE_CREATED: "blue",
    SURVEY_PROCESS: "purple",
    WORKSHOP_REPAIR: "orange",
    DELIVERY_PAYMENT: "teal",
    CLAIM_SETTLEMENT: "green",
  })[stage] || "gray";
export const statusBadge = (s) => {
  if (!s) return "gray";
  // Blue — case creation phase
  if (
    [
      "CASE_REGISTERED",
      "CUSTOMER_REGISTERED",
      "DOCUMENTS_PENDING",
      "DOCUMENTS_SUBMITTED",
    ].includes(s)
  )
    return "blue";
  // Purple — survey phase
  if (
    [
      "SPOT_SURVEY_ASSIGNED",
      "SPOT_SURVEY_COMPLETED",
      "SECOND_SURVEY_COMPLETED",
      "FINAL_SURVEY_COMPLETED",
    ].includes(s)
  )
    return "purple";
  // Orange — active repair
  if (
    [
      "WORKSHOP_ASSIGNED",
      "VEHICLE_PICKED",
      "VEHICLE_RECEIVED_AT_WORKSHOP",
      "DISMANTLING_APPROVED",
      "DISMANTLING_COMPLETED",
      "WORK_STARTED",
      "PART_ORDERED",
      "PART_DELIVERED",
      "TRUCK_FINALIZED",
      "ROAD_TRIAL_COMPLETED",
      "READY_FOR_INSPECTION",
    ].includes(s)
  )
    return "orange";
  // Teal — delivery & payment
  if (
    ["VEHICLE_DELIVERED", "PAYMENT_REQUEST_SENT", "PAYMENT_COMPLETED"].includes(
      s,
    )
  )
    return "teal";
  // Green — settled / closed
  if (["CLAIM_SETTLED", "CASE_CLOSED"].includes(s)) return "green";
  return "gray";
};
export const docBadge = (s) =>
  ({ VERIFIED: "green", APPROVED: "green", REJECTED: "red" })[s] || "yellow";

// ─── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ children, variant = "gray" }) {
  return <span className={`wd-badge wd-badge-${variant}`}>{children}</span>;
}
export function expiryLabel(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil(
    (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24),
  );
  if (diff < 0) return { text: "Expired", color: "var(--wd-red)" };
  if (diff < 30) return { text: "Expiring soon", color: "var(--wd-orange)" };
  return null;
}
export function ulipBadgeVariant(status) {
  return (
    {
      VALID: "green",
      EXPIRING_SOON: "yellow",
      EXPIRED: "red",
      MISMATCH: "red",
      BLACKLISTED: "red",
      NOT_APPLICABLE: "gray",
    }[status] || "gray"
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
export function Avatar({ name = "", size = 32 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        background: getAvatarColor(name),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        color: "#fff",
      }}
    >
      {getInitials(name)}
    </div>
  );
}

const SOURCE_META = {
  APP: { label: "App", icon: "📱", color: "var(--wd-blue)" },
  WHATSAPP_BOT: { label: "WhatsApp", icon: "🤖", color: "var(--wd-green)" },
  ADMIN: { label: "Admin", icon: "🖥️", color: "var(--wd-t3)" },
};
export const SOURCE_LABELS = Object.fromEntries(
  Object.entries(SOURCE_META).map(([k, v]) => [k, v.label]),
);
export const sourceMeta = (src) =>
  SOURCE_META[src] || {
    label: humanise(src || ""),
    icon: "❓",
    color: "var(--wd-t3)",
  };

// ─── PriorityBadge ────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  Minor: {
    color: "#22c55e",
    bg: "rgba(34,197,94,.12)",
    border: "rgba(34,197,94,.25)",
    icon: "○",
  },
  Average: {
    color: "#3b82f6",
    bg: "rgba(59,130,246,.12)",
    border: "rgba(59,130,246,.25)",
    icon: "◐",
  },
  Major: {
    color: "#f97316",
    bg: "rgba(249,115,22,.12)",
    border: "rgba(249,115,22,.25)",
    icon: "●",
  },
  Serious: {
    color: "#ef4444",
    bg: "rgba(239,68,68,.12)",
    border: "rgba(239,68,68,.25)",
    icon: "⬤",
  },
};
export function PriorityBadge({ priority }) {
  const p = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Minor;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 10,
        background: p.bg,
        color: p.color,
        border: `1px solid ${p.border}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 8 }}>{p.icon}</span> {priority}
    </span>
  );
}

// ─── SLABadge ─────────────────────────────────────────────────────────────────
export function SLABadge({ status }) {
  if (status === "BREACHED")
    return (
      <span
        style={{
          color: "var(--wd-red)",
          fontSize: 12,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        Breached
      </span>
    );
  return (
    <span
      style={{
        color: "var(--wd-green)",
        fontSize: 12,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
      On Time
    </span>
  );
}

// ─── StageBar ─────────────────────────────────────────────────────────────────
const STAGES = [
  "CASE_CREATED",
  "SPOT SURVEY",
  "WORKSHOP_REPAIR",
  "SURVEY_PROCESS",
  "DELIVERY_PAYMENT",
  "CLAIM_SETTLEMENT",
];
export function StageBar({ currentStage }) {
  const idx = STAGES.indexOf(currentStage);
  return (
    <div style={{ display: "flex", gap: 5 }}>
      {STAGES.map((s, i) => (
        <div
          key={s}
          title={STAGE_LABELS[s]}
          style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: i <= idx ? "var(--wd-blue)" : "var(--wd-border-md)",
            transition: "background .2s",
          }}
        />
      ))}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ full = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: full ? 0 : "60px 0",
        height: full ? "100%" : "auto",
      }}
    >
      <div className="wd-spinner" />
    </div>
  );
}

export function TopLoader() {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        background: "linear-gradient(90deg, var(--wd-blue), #a855f7)",
        animation: "wd-toploader 1.2s ease-in-out infinite",
      }}
    />
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────────────────
export function InfoRow({ label, value, mono = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span
        style={{
          fontSize: 11,
          color: "var(--wd-t3)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".05em",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: "var(--wd-t1)",
          fontFamily: mono ? "var(--wd-mono)" : "var(--wd-sans)",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

// ─── SectionCard ─────────────────────────────────────────────────────────────
export function SectionCard({ title, children, action }) {
  return (
    <div className="wd-card" style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>
          {title}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 520,
}) {
  if (!open) return null;
  return createPortal(
    <div className="wd-overlay" onClick={onClose}>
      <div
        className="wd-modal wd-animate"
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wd-modal-header">
          <span
            style={{ fontWeight: 700, fontSize: 15, color: "var(--wd-t1)" }}
          >
            {title}
          </span>
          <button
            className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
            onClick={onClose}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="wd-modal-body">{children}</div>
        {footer && <div className="wd-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.querySelector(".wd-root"),
  );
}

// ─── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: 20,
      }}
    >
      <div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "var(--wd-t1)",
            margin: 0,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p style={{ margin: "3px 0 0", fontSize: 13, color: "var(--wd-t3)" }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

// ─── SearchInput ─────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = "Search..." }) {
  return (
    <div style={{ position: "relative", minWidth: 220 }}>
      <svg
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--wd-t3)",
        }}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        className="wd-input"
        style={{ paddingLeft: 32 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

// ─── RatingStars ─────────────────────────────────────────────────────────────
export function RatingStars({ rating }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="var(--wd-yellow)"
        stroke="none"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      <span style={{ fontSize: 12, color: "var(--wd-t2)", fontWeight: 500 }}>
        {rating}
      </span>
    </span>
  );
}

// ─── WorkloadBadge ───────────────────────────────────────────────────────────
export function WorkloadBadge({ load }) {
  return (
    <span
      className={`wd-badge wd-badge-${{ HIGH: "red", MEDIUM: "yellow", LOW: "green" }[load] || "gray"}`}
    >
      {load}
    </span>
  );
}

// ─── StatCard (colored KPI) ───────────────────────────────────────────────────
export function StatCard({ label, value, color }) {
  return (
    <div
      style={{
        background: color,
        borderRadius: 14,
        padding: 20,
        color: "#fff",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "rgba(255,255,255,.75)",
          fontWeight: 500,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>
        {value ?? "—"}
      </div>
      <div className="wd-kpi-bar" />
    </div>
  );
}

// ─── MetricCard (with border accent) ─────────────────────────────────────────
export function MetricCard({ label, value, color }) {
  return (
    <div
      className="wd-card"
      style={{ padding: 16, borderLeft: `3px solid ${color}` }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--wd-t3)",
          textTransform: "uppercase",
          letterSpacing: ".05em",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value ?? 0}</div>
    </div>
  );
}

// ─── EmptyState ──────────────────────────────────────────────────────────────
export function EmptyState({ message = "No data found" }) {
  return (
    <div className="wd-empty">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

// ─── ErrorState ──────────────────────────────────────────────────────────────
export function ErrorState({ message = "Something went wrong", onRetry }) {
  return (
    <div className="wd-empty">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--wd-red)"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span style={{ color: "var(--wd-red)" }}>{message}</span>
      {onRetry && (
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}
